import React, { useState, useEffect } from 'react';
import {
  TextField,
  Grid,
  MenuItem,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Chip,
  Box,
} from '@mui/material';
import { Person, Phone, Home, LocationOn } from '@mui/icons-material';
import FormDialog from '../Common/FormDialog';
import api from '../../utils/api';
import { Agent, BuildingCaretaker } from '../../types';
import ToastNotification from '../../utils/toastNotification';

interface EditBuildingCaretakerDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  caretaker: BuildingCaretaker | null;
}

interface FormData {
  name: string;
  phone: string;
  governorate: string;
  district: string;
  town: string;
  buildingName: string;
  notes: string;
  agentIds: string[];
}

const LEBANON_GOVERNORATES = [
  'Beirut',
  'Mount Lebanon',
  'North Lebanon',
  'South Lebanon',
  'Nabatieh',
  'Beqaa',
  'Baalbek-Hermel',
  'Akkar',
];

const DISTRICT_MAP: Record<string, string[]> = {
  'Beirut': ['Beirut'],
  'Mount Lebanon': ['Baabda', 'Aley', 'Chouf', 'Keserwan', 'Jbeil', 'Matn'],
  'North Lebanon': ['Tripoli', 'Batroun', 'Bsharri', 'Koura', 'Miniyeh-Danniyeh', 'Zgharta'],
  'South Lebanon': ['Sidon', 'Tyre', 'Jezzine'],
  'Nabatieh': ['Nabatieh', 'Bint Jbeil', 'Hasbaya', 'Marjeyoun'],
  'Beqaa': ['Zahle', 'West Beqaa', 'Rashaya'],
  'Baalbek-Hermel': ['Baalbek', 'Hermel'],
  'Akkar': ['Akkar'],
};

const EditBuildingCaretakerDialog: React.FC<EditBuildingCaretakerDialogProps> = ({
  open,
  onClose,
  onSuccess,
  caretaker,
}) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    phone: '',
    governorate: '',
    district: '',
    town: '',
    buildingName: '',
    notes: '',
    agentIds: [],
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [districtOptions, setDistrictOptions] = useState<string[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);

  useEffect(() => {
    if (open) {
      loadAgents();
      if (caretaker) {
        setFormData({
          name: caretaker.name || '',
          phone: caretaker.phone || '',
          governorate: caretaker.governorate || '',
          district: caretaker.district || '',
          town: caretaker.town || '',
          buildingName: caretaker.buildingName || '',
          notes: caretaker.notes || '',
          agentIds: caretaker.agents?.map((a) => a.id) || [],
        });
        setErrors({});
        if (caretaker.governorate) {
          setDistrictOptions(DISTRICT_MAP[caretaker.governorate] || []);
        } else {
          setDistrictOptions([]);
        }
      }
    }
  }, [open, caretaker]);

  const loadAgents = async () => {
    try {
      const response = await api.get('/agents');
      setAgents(response.data);
    } catch (error) {
      console.error('Error loading agents:', error);
    }
  };

  const handleChange = (field: keyof FormData, value: any) => {
    if (field === 'governorate') {
      const districts = value ? DISTRICT_MAP[value] || [] : [];
      setDistrictOptions(districts);
      setFormData((prev) => ({ ...prev, governorate: value, district: '' }));
      if (errors['governorate']) setErrors((prev) => ({ ...prev, governorate: '' }));
      return;
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as string]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
      ToastNotification.error('Please enter the caretaker name');
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
      ToastNotification.error('Please enter the phone number');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm() || !caretaker) return;
    setIsSubmitting(true);
    try {
      const payload = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        governorate: formData.governorate || undefined,
        district: formData.district || undefined,
        town: formData.town || undefined,
        buildingName: formData.buildingName || undefined,
        notes: formData.notes || undefined,
        agentIds: formData.agentIds,
      };
      await api.put(`/building-caretakers/${caretaker.id}`, payload);
      ToastNotification.success('Building caretaker updated successfully!');
      onSuccess();
      onClose();
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message || 'Failed to update caretaker';
      ToastNotification.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormDialog
      open={open}
      title="Edit Building Caretaker"
      subtitle="Update caretaker information"
      onClose={onClose}
      onSubmit={handleSubmit}
      maxWidth="sm"
      submitText="Save Changes"
      isSubmitting={isSubmitting}
      disableSubmit={isSubmitting}
    >
      <Grid container spacing={2.5}>
        {/* Name */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Full Name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        {/* Phone */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Phone Number"
            value={formData.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            error={!!errors.phone}
            helperText={errors.phone}
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Phone fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        {/* Governorate */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Governorate</InputLabel>
            <Select
              value={formData.governorate}
              label="Governorate"
              onChange={(e) => handleChange('governorate', e.target.value)}
              startAdornment={
                <InputAdornment position="start">
                  <LocationOn fontSize="small" />
                </InputAdornment>
              }
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {LEBANON_GOVERNORATES.map((gov) => (
                <MenuItem key={gov} value={gov}>
                  {gov}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* District */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth disabled={!formData.governorate}>
            <InputLabel>District</InputLabel>
            <Select
              value={formData.district}
              label="District"
              onChange={(e) => handleChange('district', e.target.value)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {districtOptions.map((d) => (
                <MenuItem key={d} value={d}>
                  {d}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Town */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Town / Village"
            value={formData.town}
            onChange={(e) => handleChange('town', e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocationOn fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        {/* Building Name */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Building Name"
            value={formData.buildingName}
            onChange={(e) => handleChange('buildingName', e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Home fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        {/* Assign Agents */}
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="agents-label">Assign Agents (Optional)</InputLabel>
            <Select
              labelId="agents-label"
              multiple
              value={formData.agentIds}
              onChange={(e) => handleChange('agentIds', e.target.value)}
              input={<OutlinedInput label="Assign Agents (Optional)" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((id) => {
                    const agent = agents.find((a) => a.id === id);
                    const name = agent?.user
                      ? `${agent.user.firstName} ${agent.user.lastName}`
                      : 'Unknown Agent';
                    return <Chip key={id} label={name} size="small" />;
                  })}
                </Box>
              )}
            >
              {agents.map((agent) => (
                <MenuItem key={agent.id} value={agent.id}>
                  <Checkbox checked={formData.agentIds.includes(agent.id)} />
                  <ListItemText
                    primary={
                      agent.user
                        ? `${agent.user.firstName} ${agent.user.lastName}`
                        : 'Unknown Agent'
                    }
                    secondary={agent.user?.phone || ''}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Notes */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Notes"
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            multiline
            rows={3}
            placeholder="Any additional notes about this caretaker..."
          />
        </Grid>
      </Grid>
    </FormDialog>
  );
};

export default EditBuildingCaretakerDialog;
